import React, { useMemo } from "react";
import {
  Header,
  Section,
  SectionHeader,
  PlanContainer,
  PlanDetails,
  PlanName,
  PlanPrice,
  MainContainer,
  InnerContainer,
  PlanDes,
  Price,
  PricePeriod,
  PlanIncludesDes,
  ButtonWrapper,
  StyledButton,
  PriceWrapper,
} from "./styles";
import CheckIcon from "@/assets/icons/checkIcon";
import cache from "utils/cache";
import { useVerifyUserQuery } from "api/authApi";
import { useNavigate } from "react-router-dom";
import Loader from "shared/Loader";

const SubscriptionBilling = () => {
  const navigate = useNavigate();
  const { data: verifiedUser, isLoading } = useVerifyUserQuery();
  const user = verifiedUser || cache.getItem("user");
  const subscription = user?.subscription;
  const isSponsored = Boolean(user?.isSponsored);

  const subscriptionUi = useMemo(() => {
    const ACTIVE_STATUSES = ["ACTIVE", "APPROVAL_PENDING"];
    const isActive = Boolean(
      subscription && ACTIVE_STATUSES.includes(subscription?.status)
    );
    if (isSponsored && !subscription) {
      return {
        planName: "Sponsored access",
        statusLabel: "SPONSORED",
        billingLabel: "Sponsored",
        priceLabel: "$0.00",
        periodLabel: "",
        description:
          "Your account is sponsored. You can use the platform without a paid subscription.",
        includes:
          "Product imports, order automation, and store tools are available without a paid plan.",
        showCheck: true,
      };
    }

    if (!subscription) {
      return {
        planName: "No active plan",
        statusLabel: "INACTIVE",
        billingLabel: "Monthly Plan",
        priceLabel: "$0.00",
        periodLabel: "/ monthly",
        description:
          "You are currently not subscribed. Pick a plan to unlock full dropship features.",
        includes:
          "Product imports, order automation, and advanced analytics are available after activating a plan.",
        showCheck: false,
      };
    }

    const cycle = subscription?.plan === "YEARLY" ? "Yearly Plan" : "Monthly Plan";
    const fallbackPrice =
      subscription?.planType === "PRO"
        ? subscription?.plan === "YEARLY"
          ? "$509.90"
          : "$49.99"
        : subscription?.plan === "YEARLY"
        ? "$305.90"
        : "$29.99";
    const exactPrice =
      typeof subscription?.price === "number"
        ? `$${Number(subscription.price).toFixed(2)}`
        : fallbackPrice;

    return {
      planName: subscription?.planType || "Subscribed Plan",
      statusLabel: subscription?.status || "UNKNOWN",
      billingLabel: cycle,
      priceLabel: exactPrice,
      periodLabel: subscription?.plan === "YEARLY" ? "/ yearly" : "/ monthly",
      description: isActive
        ? "Your account billing is active and follows the selected subscription plan."
        : "Your subscription exists but is not active yet. Please complete activation or update plan.",
      includes:
        "Rights Registrations & Metadata Delivery, Unlimited Users, 100,000 Songs, Free ISRCs, Unlimited Recordings, Unlimited Releases, 1000 GB Storage, Unlimited Documents & Contacts, 100% Direct Royalty Payouts, PurchaseChat & E-mail Support",
      showCheck: isActive,
    };
  }, [subscription, isSponsored]);

  if (isLoading) {
    return <Loader isLoading={true} />;
  }

  return (
    <MainContainer>
      <InnerContainer>
        <Header>Subscription And Billing</Header>

        <Section>
          <ButtonWrapper>
            <SectionHeader>Current Plan</SectionHeader>
            {!isSponsored && (
              <StyledButton onClick={() => navigate("/pricing")}>
                Change Payment Plan
              </StyledButton>
            )}
          </ButtonWrapper>
          <PlanContainer>
            <PlanDetails>
              <PlanName>{subscriptionUi.planName}</PlanName>
              <PlanDes>{subscriptionUi.description}</PlanDes>
              <PlanDes>{`Status: ${subscriptionUi.statusLabel}`}</PlanDes>
              <PriceWrapper>
                <div>
                  <PlanPrice>{subscriptionUi.billingLabel} /</PlanPrice>{" "}
                  <Price>{subscriptionUi.priceLabel} </Price>
                  <PricePeriod>{subscriptionUi.periodLabel}</PricePeriod>
                </div>
                {subscriptionUi.showCheck ? <CheckIcon /> : null}
              </PriceWrapper>
              <PlanIncludesDes>{`Includes: ${subscriptionUi.includes}`}</PlanIncludesDes>
            </PlanDetails>
          </PlanContainer>
        </Section>
      </InnerContainer>
    </MainContainer>
  );
};

export default SubscriptionBilling;
